// The customer directory - route /customers.
//
// TWO QUESTIONS, and this page answers the first: who is this caller? One search
// box resolves them from whatever they say - an account number, a phone number in
// any format, a name, an email, a company - with no mode switch (design §1). The
// second question, "what is already open for them", is Detail.jsx.
//
// WHAT THE UNFILTERED FIRST PAGE IS FOR, since it is not agent browsing. Zendesk's
// "Location" lookup has `relationship_target_type: "zen:organization"` - over there
// organizations and locations are the same table - so our two-level org → store
// tree has to be DERIVED at migration time, and a derivation contains errors a
// human has to find and fix. The alphabetical page is that correction surface. An
// agent mid-call uses the search box; nobody pages through a franchise directory.
// If the first real import turns out to be 80,000 contacts rather than 800, the
// landing state should become "recently in touch" instead - that is a one-line
// change to DEFAULT_SORT, and it is a decision for after the import (design §7.1).
//
// Filter state lives in the URL, not in component state, so a scoped directory is a
// link an agent can send and Back steps through filter changes.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { api } from '../../api/client.js';
import CustomerFilters from './CustomerFilters.jsx';
import { visibleCustomerColumns } from './customer-columns.js';
import { useAppSettings } from '../../hooks/useAppSettings.js';
import {
  PAGE_SIZE,
  emptyMessage,
  readFilters,
  toApiQuery,
  toSearchParams,
} from './customer-query.js';
import { supportPath } from '../../support-path.js';

// The column strategy, in one place.
//
// `table-fixed` plus this colgroup is what stops the horizontal scrollbar. Under
// `table-layout: auto` the table's width is max(container, sum of per-column
// min-content), and a nowrap cell's min-content is its full single-line text.
//
// THE ARITHMETIC. The invariant is: the sum of the FIXED-px columns must be less
// than the NARROWEST table box, and the percentages must not over-subscribe what is
// left.
//
//   * The narrowest table box is 960px. AppLayout renders
//     `<main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">`, so at
//     viewport 1024 - where `lg:` first applies and the layout is tightest - that
//     is 1024 − 64 = 960. Unlike /tickets there is NO 224px sidebar and no 24px
//     gap, because this screen has no view picker (design §6: there are no customer
//     views, and the ticket-side view registry is entirely ticket-scoped). The
//     queue's own comment does the same subtraction and lands on ~710px; dropping
//     the sidebar is the whole difference.
//   * Fixed columns: 7.5 + 8.5 + 4.5 = 20.5rem = 328px, and 328 < 960. ✓
//   * 328px is 34.2% of 960. The percentage columns claim 21 + 21 + 12 + 11 = 65%.
//     Total 99.2% - under 100, so nothing is scaled down and nothing overflows.
//   * At the 1216px full width the fixed columns hold their rem values and the
//     percentages absorb the extra, which is the right way round: Phone and Open
//     have a content ceiling, Name and Email do not.
//
// Per-column: Position at 120px fits "assistant manager" truncated, which is enough
// to tell an owner from a driver; Phone at 136px fits "(555) 555-0142" at text-sm;
// Open at 72px is the queue's Ref width and holds a two-digit count.
/**
 * One renderer per column key, so the body cells stop being POSITIONAL.
 *
 * The catalogue, the labels and the widths live in ./customer-columns.js; which of them
 * appear and in what order is `customers.default_columns` in `app_settings`, chosen on
 * the admin Settings screen. Tickets keep their arrangement on a saved view; there are
 * no saved views for customers, which is why that table exists.
 *
 * Every cell's markup is unchanged from the positional version, comments included. The
 * `key` prop is on the <td> because these are rendered from an array now — and the
 * hazard the old comment here recorded (a cell at the wrong index silently rendering
 * under the wrong header at the wrong width) cannot happen at all any more.
 */
const CELLS = {
  name: (customer) => (
    <td key="name" className="px-4 py-2">
      <Link
        to={supportPath(`/customers/${customer.id}`)}
        title={customer.name ?? customer.email ?? '(no name)'}
        className="block truncate font-medium text-ink hover:text-brand-700 dark:hover:text-brand-300"
      >
        {customer.name ?? customer.email ?? '(no name)'}
      </Link>
    </td>
  ),

  position: (customer) => (
    <td
      key="position"
      className="truncate px-4 py-2 text-ink-secondary"
      title={customer.position ?? undefined}
    >
      {customer.position ?? <Absent label="No position recorded" />}
    </td>
  ),

  phone: (customer) => (
    <td key="phone" className="whitespace-nowrap px-4 py-2 text-ink-secondary">
      {customer.phone ?? <Absent label="No phone number" />}
    </td>
  ),

  email: (customer) => (
    <td key="email" className="truncate px-4 py-2 text-ink-secondary" title={customer.email ?? undefined}>
      {customer.email ?? <Absent label="No email address" />}
    </td>
  ),

  // Plain text, not a link: there is no organization page in v1.
  organization: (customer) => (
    <td
      key="organization"
      className="truncate px-4 py-2 text-ink-secondary"
      title={customer.organization?.name ?? undefined}
    >
      {customer.organization?.name ?? <Absent label="No organization" />}
    </td>
  ),

  store: (customer) => (
    <td
      key="store"
      className="truncate px-4 py-2 text-ink-secondary"
      // The account number rides in the title, so it is recoverable
      // without spending a column on it.
      title={
        customer.location
          ? [customer.location.name, customer.location.account_number]
              .filter(Boolean)
              .join(' · ')
          : undefined
      }
    >
      {customer.location?.name ?? <Absent label="No store" />}
    </td>
  ),

  // NOT a badge. --color-sunken is byte-identical to slate-100 and is
  // the row's own hover fill, so a chip filled bg-sunken dissolves
  // into the row exactly when the pointer is on it. A count does not
  // need a chip.
  open: (customer) => (
    <td key="open" className="whitespace-nowrap px-4 py-2 tabular-nums">
      {customer.open_ticket_count > 0 ? (
        <span className="font-medium text-ink">{customer.open_ticket_count}</span>
      ) : (
        <Absent label="No open tickets" />
      )}
    </td>
  ),
};

function EmptyRow({ span, children }) {
  return (
    <tr>
      {/* Derived, never a literal: a hand-kept count survives right up until
          someone adds a column. */}
      <td colSpan={span} className="px-4 py-12 text-center text-sm text-ink-muted">
        {children}
      </td>
    </tr>
  );
}

/**
 * An absent value. "-" announces as nothing useful, so the meaning goes in
 * `sr-only` text rather than in a `title`, which is not keyboard reachable, is
 * unreliably announced, and never appears on touch.
 *
 * The muted class promotes on hover: `text-ink-muted` is 4.76:1 on `bg-surface` but
 * only 4.34:1 on `bg-sunken` in LIGHT mode - the hovered row would otherwise drop
 * this text under AA.
 */
function Absent({ label }) {
  return (
    <>
      <span aria-hidden="true" className="text-ink-muted group-hover:text-ink-secondary">
        -
      </span>
      <span className="sr-only">{label}</span>
    </>
  );
}

export default function CustomerList() {
  const settings = useAppSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const [result, setResult] = useState({ status: 'loading', rows: [], page: null, error: null });
  const [locations, setLocations] = useState({ status: 'idle', rows: [] });
  const [reloadToken, setReloadToken] = useState(0);

  const query = toApiQuery(filters);

  useEffect(() => {
    const controller = new AbortController();
    setResult((previous) => ({ ...previous, status: 'loading', error: null }));

    api
      .get(`/customers/list?${query}`, { signal: controller.signal })
      .then((payload) => {
        setResult({
          status: 'ready',
          rows: payload?.data ?? [],
          page: payload?.page ?? null,
          error: null,
        });
      })
      .catch((error) => {
        if (controller.signal.aborted || error.name === 'AbortError') return;
        setResult({ status: 'error', rows: [], page: null, error });
      });

    return () => controller.abort();
  }, [query, reloadToken]);

  // The store list depends only on the chosen organization, so it is fetched on
  // that alone rather than on every filter change. A failure here is not fatal: the
  // directory still works scoped to the organization, so the picker says so and
  // gets out of the way.
  useEffect(() => {
    if (!filters.organizationId) {
      setLocations({ status: 'idle', rows: [] });
      return undefined;
    }
    const controller = new AbortController();
    setLocations({ status: 'loading', rows: [] });
    api
      .get(`/organizations/${encodeURIComponent(filters.organizationId)}/locations`, {
        signal: controller.signal,
      })
      .then((payload) => setLocations({ status: 'ready', rows: payload?.data ?? [] }))
      .catch((error) => {
        if (controller.signal.aborted || error.name === 'AbortError') return;
        setLocations({ status: 'error', rows: [] });
      });
    return () => controller.abort();
  }, [filters.organizationId]);

  const updateFilters = useCallback(
    (patch) => {
      // Any non-offset filter change resets paging: page 3 of the old result set is
      // meaningless against the new one. Only an explicit page step carries an
      // offset through.
      const offset = 'offset' in patch ? patch.offset : 0;
      setSearchParams(toSearchParams({ ...filters, ...patch, offset }));
    },
    [filters, setSearchParams],
  );

  // Which columns this list shows, chosen once by an admin on the Settings screen.
  // There are no saved views for customers, so `app_settings` is the only place the
  // arrangement can live — that is the reason migration 026 exists. Nothing stored
  // resolves to the full catalogue, so the list an agent already knows is the default.
  const columns = useMemo(() => visibleCustomerColumns(settings['customers.default_columns']),
    [settings]);

  const page = result.page;
  const showingFrom = page && page.total > 0 ? page.offset + 1 : 0;
  const showingTo = page ? page.offset + result.rows.length : 0;

  return (
    // Not the queue's lg:flex-row split, because there is no sidebar. (If one ever
    // arrives, remember min-w-0 on the content column: it is what lets the table's
    // own overflow-x-auto scroll instead of widening the whole page.)
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Customers</h1>
        <div className="flex items-center gap-3">
          {page && (
            <p className="text-sm text-ink-muted">
              {page.total.toLocaleString()} {page.total === 1 ? 'contact' : 'contacts'}
            </p>
          )}
          {/* The read the roles feature exists for: reach a group on purpose. */}
          <Link to={supportPath('/customers/send-lists')} className="btn-ghost ring-1 ring-line-strong">
            Send lists
          </Link>
        </div>
      </div>

      <CustomerFilters
        filters={filters}
        onChange={updateFilters}
        locations={locations.rows}
        locationStatus={locations.status}
      />

      {result.status === 'error' && (
        // There is no semantic error-surface token, and this is the third site to
        // spell the red steps out (with the queue and New.jsx). A 50 fill / 200
        // border / 800 text card inverts to a 950 fill with a 900 border and
        // 200/300 text, which no single token can express - if a FOURTH site
        // appears, that is the moment to add one, in @theme static AND .dark.
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            Could not load the directory.
          </p>
          <p className="mt-1 text-sm text-red-700 dark:text-red-300">{result.error?.message}</p>
          <button
            type="button"
            className="btn-ghost mt-3 ring-1 ring-red-300 dark:ring-red-800"
            onClick={() => setReloadToken((token) => token + 1)}
          >
            Try again
          </button>
        </div>
      )}

      {/* No zebra striping. A third grey collapses the surface → sunken → hover
          steps that the header row and the hover state both depend on. */}
      <div className="overflow-x-auto rounded-lg border border-line bg-surface">
        <table className="w-full table-fixed divide-y divide-line text-sm">
          <colgroup>
            {columns.map((col) => (
              <col key={col.key} className={col.width} />
            ))}
          </colgroup>
          <thead className="bg-sunken text-left text-xs font-medium uppercase tracking-wide text-ink-secondary">
            <tr>
              {columns.map((col) => (
                <th key={col.key} scope="col" className="truncate px-4 py-2.5">
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {/* Previous rows stay on screen during a refetch - blanking the table on
                every filter change makes a fast list feel slower than it is. */}
            {result.status === 'loading' && result.rows.length === 0 && (
              <EmptyRow span={columns.length}>Loading…</EmptyRow>
            )}

            {result.status === 'ready' && result.rows.length === 0 && (
              <EmptyRow span={columns.length}>{emptyMessage(filters)}</EmptyRow>
            )}

            {result.rows.map((customer) => (
              // `key` is the id and NEVER the email. Since migration 018 `email` is
              // nullable and its unique index is filtered, so two contacts with no
              // address are two ordinary rows - keyed on email they would be one.
              <tr key={customer.id} className="group transition-colors hover:bg-sunken">
                {columns.map((col) => CELLS[col.key]?.(customer) ?? null)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {page && page.total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-ink-muted">
            Showing {showingFrom.toLocaleString()}–{showingTo.toLocaleString()} of{' '}
            {page.total.toLocaleString()}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={page.offset === 0}
              onClick={() => updateFilters({ offset: Math.max(0, page.offset - PAGE_SIZE) })}
            >
              Previous
            </button>
            <button
              type="button"
              className="btn-ghost ring-1 ring-line-strong"
              disabled={!page.has_more}
              onClick={() => updateFilters({ offset: page.offset + PAGE_SIZE })}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
